import useFinalizeLifecareSummary from '../hooks/useFinalizeLifecareSummary';
import { caseListContainerClasses } from './common/caseListClasses';
import XLoader from './common/XLoader';
import RenderIf from './render-if/Renderif';
import CaseFileUpload from './common/CaseFileUpload';
import CaseSummaryHeader from './CaseSummaryHeader';
import LifeCarePlanReassignment from './LifeCarePlanReassignment';
import LifeCarePlanSupportingDocumentsSection from './LifeCarePlanSupportingDocumentsSection';
import FinalizeFailedBanner from './FinalizeFailedBanner';
import TooltipWrapper from './ui-control/TooltipWrapper';
import Dialog from './ui-control/Dialog';
import XButton from './common/XButton';
import ChatWindow from '../pages/chatBot/ChatWindow';
import { useChatToggle } from '../contexts/ChatToggleContext';
import { RECORD_DATA_TYPE } from '@demandproai/shared';
import RecordFileUpload from './RecordFileUpload';
import './FinalizeLifecareSummary.css';
import {
    finalSettlementHeaderClasses,
    finalSettlementTitleClasses,
    finalSettlementSubtitleClasses,
} from './FinalSettlementBaseHeader';

const spinnerClasses = "animate-spin h-8 w-8 border-[0.25em] border-primary border-t-transparent rounded-full m-3";
const dialogRowClasses = "flex justify-center mt-8";
const dialogPromptRowClasses = `${dialogRowClasses} px-4 text-center`;

const FinalizeLifecareSummary = () => {
    const {
        caseId,
        caseData,
        clients,
        files,
        selectedClientIndex,
        setSelectedClientIndex,
        isLoading,
        isSubmitting,
        isUploading,
        removingFileId,
        finalizationLabel,
        hasFailedFinalization,
        isConfirmModalOpen,
        isDisabled,
        shouldShowTooltip,
        tooltipTitle,
        finalizeButtonLabel,
        requestFinalize,
        handleFinalize,
        cancelFinalize,
        handleRemoveFile,
        handleFileSelection,
        reassignment,
        supportingDocs,
        isReplaceModalOpen,
        confirmReplaceCertifiedFile,
        cancelReplaceCertifiedFile,
    } = useFinalizeLifecareSummary();
    const { isChatOpen } = useChatToggle();

    return (
        <section className={`[&_.chatbot]:h-screen! [&_.chatbot]:pb-2.5 ${isChatOpen ? 'chat-open' : ''}`}>
            <ChatWindow caseId={caseId} isOpen={isChatOpen} />

            <div className={`container ${caseListContainerClasses}`}>
                <div className="listing">
                    <RenderIf shouldRender={isLoading}>
                        <div className="flex justify-center">
                            <XLoader />
                        </div>
                    </RenderIf>
                    <RenderIf shouldRender={!isLoading}>
                        <>
                            <CaseSummaryHeader caseId={caseId} caseData={caseData} />

                            <RenderIf shouldRender={hasFailedFinalization}>
                                <FinalizeFailedBanner />
                            </RenderIf>

                            <div className={`${finalSettlementHeaderClasses} rounded-t-sm`}>
                                <div>
                                    <h3 className={finalSettlementTitleClasses}>Finalize Lifecare Plan</h3>
                                    <p className={finalSettlementSubtitleClasses}>
                                        Confirm the lifecare plan is ready to be delivered
                                    </p>
                                    {finalizationLabel && (
                                        <p className="mb-0! text-sm text-white">
                                            {finalizationLabel}
                                        </p>
                                    )}
                                </div>
                                <TooltipWrapper
                                    showTooltip={shouldShowTooltip}
                                    title={tooltipTitle}
                                    variant="error"
                                >
                                    <span className={`inline-block ${shouldShowTooltip ? 'cursor-not-allowed' : 'cursor-default'}`}>
                                        <XButton
                                            onClick={requestFinalize}
                                            disabled={isDisabled}
                                            className={shouldShowTooltip ? 'pointer-events-none' : 'pointer-events-auto'}
                                        >
                                            {finalizeButtonLabel}
                                        </XButton>
                                    </span>
                                </TooltipWrapper>
                            </div>

                            <div className="border-4! border-primary-surface! border-t-0! p-4!">
                                <RenderIf shouldRender={clients.length > 0}>
                                    <div className="flex items-center flex-wrap px-4 py-0 rounded">
                                        {clients.map((c, i) => {
                                            const isActive = i === selectedClientIndex;
                                            return (
                                                <XButton
                                                    key={c?._id?.$oid ?? i}
                                                    variant="close"
                                                    className={`px-6! py-2! mr-2 rounded-t-md! rounded-b-none text-sm ${isActive ? `font-bold text-content bg-white!` : 'text-subtle bg-primary-surface!'}`}
                                                    onClick={() => setSelectedClientIndex(i)}
                                                    aria-pressed={isActive}
                                                >
                                                    {c?.personalInfo?.clientName || `Plaintiff ${i + 1}`}
                                                </XButton>
                                            );
                                        })}
                                    </div>
                                </RenderIf>

                                <CaseFileUpload
                                    label="Lifecare Plan"
                                    files={files}
                                    multiple={false}
                                    inputName="certified-lifecare-plan-file"
                                    onFileSelect={handleFileSelection}
                                    onRemove={handleRemoveFile}
                                    isLoading={isUploading || !!removingFileId}
                                />
                            </div>
                            <LifeCarePlanSupportingDocumentsSection {...supportingDocs} />
                            <RecordFileUpload
                                recordType={RECORD_DATA_TYPE.DEPOSITION_DOC}
                                caseId={caseId}
                            />
                            <LifeCarePlanReassignment {...reassignment} />
                        </>
                    </RenderIf>
                </div>
            </div>

                <Dialog
                    isModalOpen={isConfirmModalOpen}
                    onToggleDialog={(isOpen) => { if (!isOpen) cancelFinalize(); }}
                    mode="small-half"
                    isCloseIconHidden={false}
                >
                    <div className={dialogRowClasses}>
                        <h3 className="title">This plan has already been finalized. Are you sure you want to re-finalize?</h3>
                    </div>
                    <div className={dialogRowClasses}>
                        {isSubmitting ? (
                            <div className={spinnerClasses} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        ) : (
                            <XButton onClick={handleFinalize} className="min-w-24!">Yes</XButton>
                        )}
                        <XButton variant="outlinePrimary" onClick={cancelFinalize} className="ml-4! min-w-24!">No</XButton>
                    </div>
                </Dialog>

                <Dialog
                    isModalOpen={isReplaceModalOpen}
                    onToggleDialog={(isOpen) => { if (!isOpen) cancelReplaceCertifiedFile(); }}
                    mode="small-half"
                    isCloseIconHidden={false}
                >
                    <div className={dialogPromptRowClasses}>
                        <h3 className="title">Replace the existing Lifecare Plan file for this plaintiff?</h3>
                    </div>
                    <div className={dialogRowClasses}>
                        <XButton onClick={confirmReplaceCertifiedFile} className="min-w-24!">Replace</XButton>
                        <XButton variant="outlinePrimary" onClick={cancelReplaceCertifiedFile} className="ml-4! min-w-24!">Cancel</XButton>
                    </div>
                </Dialog>

        </section>
    );
};

export default FinalizeLifecareSummary;
